import { KpiType, StorePerformanceInterval } from '../../constants';
import { Icons } from '../../stories/atoms';
import { UserPermissions } from '../../utils';
import { CategoryIcon } from './Home';
import { CategoryWidgetOrderMap, WidgetType, WidgetsCategory } from './types';

export const kpiTypeToWidgetTypeMap: { [key in KpiType]: WidgetType | null } = {
  dailyConversionRate: WidgetType.ConversionRate,
  dailyNewMembers: WidgetType.NewMembers,
  dailyShareOfReceipts: WidgetType.ShareOfReceipts,
  dailyTurnOver: WidgetType.HourlyTurnover,
  dailyQueueLength: WidgetType.QueueLengthWidget,
  dailySCOShareOfReceipts: WidgetType.SCOShareOfReceipts,
  dailySCOTurnOver: WidgetType.HourlyTurnover,
  dailyCustomerEngagement: WidgetType.CustomerEngagmentWidget,
  dailyRFIDAddedSales: WidgetType.RfidSalesRetagging,
};

export const widgetsCategoryToWidgetTypesMap: { [key in WidgetsCategory]: WidgetType[] } = {
  allWidgets: Object.values(WidgetType),
  customerExperience: [
    WidgetType.QueueLengthWidget,
    WidgetType.CustomerFeedBackWidget,
    WidgetType.RfidApprovalsWidget,
    WidgetType.RfidCountWidget,
    WidgetType.RfidSalesRetagging,
    WidgetType.StockAccuracyWidget,
    WidgetType.OrisCustomersWidget,
    WidgetType.CustomerEngagmentWidget,
    WidgetType.HelpASaleWidget,
    WidgetType.BopisWidget,
    WidgetType.BopisLeadTimeWidget,
  ],
  customerBaseValue: [
    WidgetType.NewMembers,
    WidgetType.ShareOfReceipts,
    WidgetType.SCOShareOfReceipts,
    WidgetType.TopSellingGarment,
    WidgetType.AppCustomersWidget,
  ],
  salesEfficiency: [
    WidgetType.AreaComparisonWidget,
    WidgetType.ConversionRate,
    WidgetType.HourlyTurnover,
    WidgetType.PiecesPerReceipt,
    WidgetType.AveragePurchaseWidget,
    WidgetType.EfficiencyWidget,
    WidgetType.SalesInsightsWidget,
  ],
  highlightedWidgets: [],
};

const getHighlightedWidgetsOrder = (highlightedWidgets: KpiType[]): CategoryWidgetOrderMap => {
  const categoryWidgetOrderMap: CategoryWidgetOrderMap = {};

  let currentOrder = 1;
  highlightedWidgets.forEach((kPITargetWidget) => {
    const widgetType = kpiTypeToWidgetTypeMap[kPITargetWidget];

    if (widgetType === null) return;

    categoryWidgetOrderMap[widgetType] = currentOrder;
    currentOrder += 1;
  });

  return categoryWidgetOrderMap;
};

const generateWidgetOrderMap = (widgets: WidgetType[]): CategoryWidgetOrderMap => {
  return widgets.reduce((acc, widget, index) => {
    acc[widget] = index + 1;
    return acc;
  }, {} as CategoryWidgetOrderMap);
};

export const createGetWidgetOrder = (
  selectedCategory: WidgetsCategory,
  highlightedWidgets: KpiType[],
): { getWidgetOrder: (widgetType: WidgetType) => number } => {
  const highlightedWidgetsOrder = getHighlightedWidgetsOrder(highlightedWidgets);

  const allWidgets = [
    WidgetType.AreaComparisonWidget,
    WidgetType.HourlyTurnover,
    WidgetType.ConversionRate,
    WidgetType.PiecesPerReceipt,
    WidgetType.AveragePurchaseWidget,
    WidgetType.EfficiencyWidget,
    WidgetType.QueueLengthWidget,
    WidgetType.RfidApprovalsWidget,
    WidgetType.RfidSalesRetagging,
    WidgetType.RfidCountWidget,
    WidgetType.StockAccuracyWidget,
    WidgetType.OrisCustomersWidget,
    WidgetType.NewMembers,
    WidgetType.ShareOfReceipts,
    WidgetType.SCOShareOfReceipts,
    WidgetType.TopSellingGarment,
    WidgetType.BopisWidget,
    WidgetType.BopisLeadTimeWidget,
    WidgetType.CustomerEngagmentWidget,
    WidgetType.HelpASaleWidget,
    WidgetType.AppCustomersWidget,
    WidgetType.CustomerFeedBackWidget,
    WidgetType.SalesInsightsWidget,
  ];

  const salesEfficiencyWidgets = [
    WidgetType.AreaComparisonWidget,
    WidgetType.HourlyTurnover,
    WidgetType.ConversionRate,
    WidgetType.PiecesPerReceipt,
    WidgetType.AveragePurchaseWidget,
    WidgetType.EfficiencyWidget,
    WidgetType.SalesInsightsWidget,
  ];

  const customerWidgets = [
    WidgetType.NewMembers,
    WidgetType.ShareOfReceipts,
    WidgetType.SCOShareOfReceipts,
    WidgetType.AppCustomersWidget,
    WidgetType.TopSellingGarment,
  ];

  const customerExperienceWidgets = [
    WidgetType.QueueLengthWidget,
    WidgetType.RfidApprovalsWidget,
    WidgetType.RfidSalesRetagging,
    WidgetType.RfidCountWidget,
    WidgetType.StockAccuracyWidget,
    WidgetType.OrisCustomersWidget,
    WidgetType.BopisWidget,
    WidgetType.BopisLeadTimeWidget,
    WidgetType.CustomerEngagmentWidget,
    WidgetType.HelpASaleWidget,
    WidgetType.CustomerFeedBackWidget,
  ];

  const allWidgetsOrder = generateWidgetOrderMap(allWidgets);
  const salesEfficiencyWidgetsOrder = generateWidgetOrderMap(salesEfficiencyWidgets);
  const customerWidgetsOrder = generateWidgetOrderMap(customerWidgets);
  const customerExperienceWidgetsOrder = generateWidgetOrderMap(customerExperienceWidgets);

  const getWidgetOrder = (widgetType: WidgetType) => {
    const widgetOrderMap: { [key in WidgetsCategory]: number } = {
      [WidgetsCategory.ALL]: allWidgetsOrder[widgetType] || 0,
      [WidgetsCategory.HIGHLIGHTED]: highlightedWidgetsOrder[widgetType] || 0,
      [WidgetsCategory.CUSTOMERBASEVALUE]: customerWidgetsOrder[widgetType] || 0,
      [WidgetsCategory.SALESEFFICIENCY]: salesEfficiencyWidgetsOrder[widgetType] || 0,
      [WidgetsCategory.CUSTOMEREXPERIENCE]: customerExperienceWidgetsOrder[widgetType] || 0,
    };

    return widgetOrderMap[selectedCategory];
  };

  return { getWidgetOrder };
};

export const getPermissonToView = (widgetType: WidgetType, userPermissions: UserPermissions): boolean => {
  const widgetTypePermissionMap: { [key in WidgetType]: boolean } = {
    [WidgetType.AveragePurchaseWidget]: userPermissions.avgPurchasePerReceiptWidget.canRead,
    [WidgetType.ConversionRate]: userPermissions.conversionRateWidget.canRead,
    [WidgetType.HourlyTurnover]: userPermissions.salesPerformanceWidget.canRead,
    [WidgetType.NewMembers]: userPermissions.newMembersWidget.canRead,
    [WidgetType.AppCustomersWidget]: userPermissions.newAppCustomersWidget.canRead,
    [WidgetType.PiecesPerReceipt]: userPermissions.avgPiecesPerReceiptWidget.canRead,
    [WidgetType.ShareOfReceipts]: userPermissions.shareOfReceiptWidget.canRead,
    [WidgetType.TopSellingGarment]: userPermissions.topSellingGarmentsWidget.canRead,
    [WidgetType.QueueLengthWidget]: userPermissions.queueLengthWidget.canRead,
    [WidgetType.AreaComparisonWidget]: userPermissions.areaComparisonWidget.canRead,
    [WidgetType.CustomerFeedBackWidget]: userPermissions.customerFeedBackWidget.canRead,
    [WidgetType.SCOShareOfReceipts]: userPermissions.scoShareOfReceipt.canRead,
    [WidgetType.OrisCustomersWidget]: userPermissions.orisCustomerWidget.canRead,
    [WidgetType.RfidApprovalsWidget]: userPermissions.rfidApprovalsWidget.canRead,
    [WidgetType.RfidCountWidget]: userPermissions.stockCountWidget.canRead,
    [WidgetType.RfidSalesRetagging]: userPermissions.rfidSalesWidget.canRead,
    [WidgetType.StockAccuracyWidget]: userPermissions.stockAccuracyWidget.canRead,
    [WidgetType.EfficiencyWidget]: userPermissions.efficiencyWidget.canRead,
    [WidgetType.CustomerEngagmentWidget]: userPermissions.customerEngagementWidget.canRead,
    [WidgetType.SalesInsightsWidget]: userPermissions.salesInsightWidget.canRead,
    [WidgetType.HelpASaleWidget]: userPermissions.helpASaleWidget.canRead,
    [WidgetType.BopisWidget]: userPermissions.bopisWidget.canRead,
    [WidgetType.BopisLeadTimeWidget]: userPermissions.bopisWidget.canRead,
  };

  return widgetTypePermissionMap[widgetType] ?? false;
};

export const createGetIsWidgetVisible = (params: {
  selectedCategory: WidgetsCategory;
  highlightedKpiTargets: KpiType[];
  userPermissions: UserPermissions;
  getIsWidgetDisabled: (widgetType: WidgetType) => boolean;
}): { getIsWidgetVisible: (widgetType: WidgetType) => boolean } => {
  const getIsWidgetVisible = (widgetType: WidgetType) => {
    const { userPermissions, selectedCategory, highlightedKpiTargets, getIsWidgetDisabled } = params;

    if (getIsWidgetDisabled(widgetType)) return false;

    const hasPermissonToView = getPermissonToView(widgetType, userPermissions);

    if (!hasPermissonToView) return false;

    const isWidgetInCategory = !!widgetsCategoryToWidgetTypesMap[selectedCategory].find((type) => {
      return type === widgetType;
    });

    if (isWidgetInCategory) return true;

    const isWidgetHighlighted = !!highlightedKpiTargets.find((kPITarget) => {
      return kpiTypeToWidgetTypeMap[kPITarget] === widgetType;
    });

    if (isWidgetHighlighted && selectedCategory === WidgetsCategory.HIGHLIGHTED) return true;

    return false;
  };

  return { getIsWidgetVisible };
};

export const isMultiDayInterval = (interval: StorePerformanceInterval) => {
  return interval === StorePerformanceInterval.WEEKLY || interval === StorePerformanceInterval.MONTHLY;
};

export const isWeeklyView = (interval: StorePerformanceInterval) => {
  return interval === StorePerformanceInterval.WEEKLY;
};

export const isMonthlyView = (interval: StorePerformanceInterval) => {
  return interval === StorePerformanceInterval.MONTHLY;
};

export const categoryIcons: CategoryIcon[] = [
  { iconId: 'highlightedWidgets', iconName: WidgetsCategory.HIGHLIGHTED, icon: Icons.STAR },
  { iconId: 'customerExperience', iconName: WidgetsCategory.CUSTOMEREXPERIENCE, icon: Icons.PROFILE },
  { iconId: 'customerBaseValue', iconName: WidgetsCategory.CUSTOMERBASEVALUE, icon: Icons.CUSTOMERVALUE },
  { iconId: 'salesEfficiency', iconName: WidgetsCategory.SALESEFFICIENCY, icon: Icons.SALESEFFICIENCY },
  { iconId: 'allWidgets', iconName: WidgetsCategory.ALL, icon: Icons.ALLWIDGETS },
];

export const getPermissionToViewCategory = (
  widgetsCategory: WidgetsCategory,
  userPermissions: UserPermissions,
): boolean => {
  const widgetTypes = widgetsCategoryToWidgetTypesMap[widgetsCategory];
  return !!widgetTypes.find((type) => getPermissonToView(type, userPermissions));
};

export const canViewHighlightedWidgets = (highlightedKpiTypes: KpiType[], userPermissions: UserPermissions) => {
  let canView = false;

  highlightedKpiTypes.forEach((kpiType) => {
    const widgetType = kpiTypeToWidgetTypeMap[kpiType];

    if (!widgetType) return;

    if (getPermissonToView(widgetType, userPermissions)) {
      canView = true;
    }
  });

  return canView;
};
